import { useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import aboutHero from "@/assets/about-hero.jpg";
import { attorneys } from "@/data/attorneys";
import type { Attorney } from "@/data/attorneys";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import AttorneySearchBar from "@/components/AttorneySearchBar";
import EmailDisclaimerDialog from "@/components/EmailDisclaimerDialog";

const getLastName = (name: string) => name.split(" ").pop() || name;

const TeamPage = () => {
  const sortedAttorneys = useMemo(() => {
    const pinned = ["joseph-e-vaughan", "tracey-a-mclean"];
    const pinnedAttorneys = pinned.map(slug => attorneys.find(a => a.slug === slug)).filter(Boolean) as Attorney[];
    const rest = attorneys.filter(a => !pinned.includes(a.slug)).sort((a, b) => getLastName(a.name).localeCompare(getLastName(b.name)));
    return [...pinnedAttorneys, ...rest];
  }, []);

  const [filtered, setFiltered] = useState<Attorney[]>(sortedAttorneys);
  const handleFilter = useCallback((results: Attorney[]) => {
    const pinned = ["joseph-e-vaughan", "tracey-a-mclean"];
    const pinnedAttorneys = pinned.map(slug => results.find(a => a.slug === slug)).filter(Boolean) as Attorney[];
    const rest = results.filter(a => !pinned.includes(a.slug)).sort((a, b) => getLastName(a.name).localeCompare(getLastName(b.name)));
    setFiltered([...pinnedAttorneys, ...rest]);
  }, []);

  return (
    <Layout>
      <SEO title="Our Team | Vaughan McLean" description="Meet the 40+ experienced attorneys at Vaughan McLean. Our team brings decades of litigation and advisory expertise across multiple practice areas." />
      <PageHero backgroundImage={aboutHero} title="Our Team" subtitle="The People" />

      <section className="section-dark py-24">
        <div className="container mx-auto px-8 lg:px-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-muted-foreground font-body max-w-2xl leading-relaxed mb-12"
          >
            Our attorneys are leaders in their respective fields, bringing a wealth of experience
            and a passion for advocacy to every client engagement.
          </motion.p>

          <AttorneySearchBar onFilter={handleFilter} />

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-muted-foreground font-body">
              {filtered.length === attorneys.length 
                ? `${attorneys.length} Attorneys`
                : `${filtered.length} of ${attorneys.length} Attorneys`
              }
            </p>
          </div>

          {filtered.length === 0 ? (
            <p className="text-muted-foreground font-body text-center py-16">No attorneys match your search criteria.</p>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
            {filtered.map((member, i) => (
              <motion.div
                key={member.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                viewport={{ once: true }}
                className="bg-card flex flex-col"
              >
                <Link
                  to={`/team/${member.slug}`}
                  className="p-8 group cursor-pointer hover:bg-secondary transition-colors duration-300 block flex-1"
                >
                   {member.photo ? (
                     <div className="w-[17rem] h-[17rem] overflow-hidden mb-6">
                       <img src={member.photo} alt={member.name} className="w-full h-full object-cover object-top" />
                     </div>
                   ) : (
                     <div className="w-[17rem] h-[17rem] border border-border flex items-center justify-center mb-6 group-hover:border-primary transition-colors">
                       <span className="font-heading text-2xl text-primary">{member.initials}</span>
                     </div>
                   )}
                  <h3 className="font-heading text-lg text-foreground mb-1">{member.name}</h3>
                  {member.title && (
                    <p className="text-xs tracking-[0.1em] uppercase font-body text-primary mb-1">{member.title}</p>
                  )}
                  {member.location && (
                    <div className="text-xs font-body text-muted-foreground mb-3 space-y-1">
                      {member.location.split(",").map((loc, idx) => (
                        <p key={idx} className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 shrink-0" />{loc.trim()}
                        </p>
                      ))}
                    </div>
                  )}
                  <span className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-body text-muted-foreground group-hover:text-primary transition-colors">
                    View Biography <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
                {(member.email || member.phone) && (
                  <div className="px-8 pb-6 pt-0 flex gap-2">
                    {member.email && (
                      <EmailDisclaimerDialog email={member.email} attorneyName={member.name} variant="compact" />
                    )}
                    {member.phone && (
                      <a
                        href={`tel:${member.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center justify-center gap-1.5 text-xs font-body text-muted-foreground hover:text-primary hover:border-primary border border-border rounded px-3 py-2 transition-colors tracking-[0.1em] uppercase flex-1"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        Call
                      </a>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default TeamPage;
